/**
 * Team-boards sync: registers a realtime engine task per team the user belongs
 * to, so boards shared with a team ride the `team:<teamId>` scope against the
 * `/sync/team/:teamId/*` endpoints and the `team:{teamId}` phoenix channel —
 * the same realtime path team workspaces use (see hooks/useWorkspaceRealtime).
 *
 * This is the counterpart to services/personalSync: private boards stay in the
 * personal scope (LWW cross-device), team boards move here (realtime, shared).
 * No-op signed out or outside the desktop app.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { teamScopeId } from '../extensions/bundled/task-manager/boardsStore';
import { addSessionEndedListener } from './channelService';

// Which teams already have a running engine task, so re-arming is cheap/idempotent.
const registered = new Set();

/**
 * Idempotent: start (once) a team-boards engine task for each team the user
 * belongs to. Safe to call on every auth/teams settle. `teams` is the array
 * from useTeamsStore (each has an `id`).
 */
export async function ensureTeamBoardsSync(teams) {
  if (!isTauri()) return;
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) return;
  for (const team of teams || []) {
    if (team?.id == null) continue;
    const teamId = String(team.id);
    const scopeId = teamScopeId(teamId);
    if (registered.has(scopeId)) continue;
    try {
      await invoke('sync_register_team', { scopeId, teamId });
      registered.add(scopeId);
    } catch (err) {
      console.warn(`[team-boards-sync] register failed for ${scopeId}:`, err);
    }
  }
}

/** Forget registrations (e.g. on sign-out) so a later sign-in re-arms cleanly. */
export function resetTeamBoardsSync() {
  registered.clear();
}

// `registered` is keyed by team scope (`team:<teamId>`), not by user — so on
// a shared device, user B logging in after user A (both members of the same
// team) saw `registered.has('team:X')` already true from A's session and
// ensureTeamBoardsSync() silently skipped `sync_register_team` for B, with no
// error. This had no call site of any kind before — hard logout, soft
// logout, or otherwise — leaving the engine-task registration state to
// outlive every session it was created for. Subscribing here means this
// module resets ITSELF on every session end, hard or soft, instead of
// depending on a call site that never existed. Registered once,
// unconditionally, at module load — see channelService.js's
// addSessionEndedListener for the invariant this closes.
addSessionEndedListener(() => {
  resetTeamBoardsSync();
});
